// Cross-origin session hand-off between the legacy (kibook-ng) and preview
// (kibook-dashboard) dashboards. Both APIs share a JWT secret, so a token minted
// by one is valid on the other — the only problem is carrying it across the origin
// the release-channel redirect crosses (localStorage can't; a parent-domain cookie can).

const HANDOFF_COOKIE = 'kb_session';
const GUARD_COOKIE = 'kb_handoff_guard';
const REDIRECT_GUARD_SECONDS = 10;
const TOKEN_KEY = 'auth_app_token';

export function decodeTokenData(jwt: string): any | null {
	try {
		const seg = jwt.split('.')[1];
		return JSON.parse(atob(seg.replace(/-/g, '+').replace(/_/g, '/'))).data;
	} catch (e) {
		return null;
	}
}

// Normalise a host for comparison (lower-case + strip a trailing FQDN dot) so a
// config value that never byte-matches the served host can't cause a redirect loop.
function normHost(host: string): string {
	return host.toLowerCase().replace(/\.$/, '');
}

// Whether redirectUrl points to a different host than currentHost — the signal to
// hand off. Unparseable / empty input → false (never redirect on bad input).
export function isForeignHost(redirectUrl: string | null | undefined, currentHost: string): boolean {
	if (!redirectUrl) { return false; }
	try {
		return normHost(new URL(redirectUrl).host) !== normHost(currentHost);
	} catch (e) {
		return false;
	}
}

// Cross-origin redirect breaker. A misconfigured customerRedirectUrl — or a server
// canonical-redirect (e.g. www→apex) the config doesn't match — could bounce
// full-page reloads between hosts forever. Stamp each redirect into a short-lived
// PARENT-DOMAIN cookie (visible across the subdomains a hand-off crosses); if one is
// already in flight, suppress + warn, degrading an infinite loop to a single
// wrong-host landing.
export function redirectOnce(redirectUrl: string, cookieDomain: string): void {
	if (guardCookiePresent()) {
		console.warn('[handoff] redirect breaker tripped — suppressing repeat redirect to ' + redirectUrl);
		return;
	}
	writeGuardCookie(cookieDomain);
	// carry the current session, so an already-logged-in user isn't sent to the other app's login page
	const jwt = storedJwt();
	if (jwt) { writeHandoffCookie(jwt, cookieDomain); }
	window.location.href = redirectUrl;
}

// At login: if the token says this customer belongs on another host, drop a
// one-time parent-domain cookie carrying the JWT and return where to send them.
// Returns null when they're already on the right host.
export function handoffRedirectUrl(jwt: string | undefined, currentHost: string, cookieDomain: string): string | null {
	if (!jwt) { return null; }
	const data = decodeTokenData(jwt);
	const redirectUrl: string | undefined = data && data.customerRedirectUrl;
	if (!isForeignHost(redirectUrl, currentHost)) { return null; }
	writeHandoffCookie(jwt, cookieDomain);
	return redirectUrl as string;
}

// At bootstrap (Nebular sets the token itself, so this only surfaces the JWT): a
// hand-off cookie means a login just happened on the other app, so it wins over any
// local token (stale or expired); consume it and return the JWT for NbTokenService.
export function adoptHandoffCookie(cookieDomain: string): string | null {
	const jwt = readHandoffCookie();
	if (!jwt) { return null; }
	clearHandoffCookie(cookieDomain);
	return jwt;
}

function storedJwt(): string | null {
	try {
		const stored = JSON.parse(localStorage.getItem(TOKEN_KEY) || 'null');
		return stored && stored.value ? stored.value : null;
	} catch (e) {
		return null;
	}
}

function writeHandoffCookie(jwt: string, cookieDomain: string): void {
	const domain = cookieDomain ? '; Domain=' + cookieDomain : '';
	document.cookie = HANDOFF_COOKIE + '=' + encodeURIComponent(jwt) + domain + '; Path=/; Max-Age=60; SameSite=Lax';
}

function readHandoffCookie(): string | null {
	const match = document.cookie.match(new RegExp('(?:^|; )' + HANDOFF_COOKIE + '=([^;]+)'));
	return match ? decodeURIComponent(match[1]) : null;
}

function clearHandoffCookie(cookieDomain: string): void {
	const domain = cookieDomain ? '; Domain=' + cookieDomain : '';
	document.cookie = HANDOFF_COOKIE + '=' + domain + '; Path=/; Max-Age=0';
}

function writeGuardCookie(cookieDomain: string): void {
	const domain = cookieDomain ? '; Domain=' + cookieDomain : '';
	document.cookie = GUARD_COOKIE + '=1' + domain + '; Path=/; Max-Age=' + REDIRECT_GUARD_SECONDS + '; SameSite=Lax';
}

function guardCookiePresent(): boolean {
	return new RegExp('(?:^|; )' + GUARD_COOKIE + '=').test(document.cookie);
}
